import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";

const STEPS: { title: string; description: string }[] = [
  { title: "Chat with AI", description: "Describe what you want to build or change here, then click Run." },
  { title: "Live Preview", description: "Your app runs here. Changes appear after each successful build." },
  { title: "Export & share", description: "Download as .zip or push to GitHub. Publish when ready." },
];

export interface WorkspaceOnboardingProps {
  step: number;
  targetRefs: React.RefObject<HTMLElement | null>[];
  onNext: () => void;
  onSkip: () => void;
}

export function WorkspaceOnboarding({ step, targetRefs, onNext, onSkip }: WorkspaceOnboardingProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const el = targetRefs[step]?.current;
    if (!el) {
      setRect(null);
      return;
    }
    const update = () => setRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("scroll", update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update, true);
    };
  }, [step, targetRefs]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
    },
    [onSkip]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const content = (
    <div className="fixed inset-0 z-[100] pointer-events-auto" aria-modal="true" role="dialog">
      {/* Backdrop with cutout at target (or full backdrop before measure) */}
      {rect ? (
        <div
          className="absolute transition-[top,left,width,height] duration-200"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
            pointerEvents: "none",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/60" aria-hidden />
      )}

      {/* Card */}
      <div
        className="absolute left-1/2 -translate-x-1/2 w-[320px] max-w-[calc(100vw-32px)] rounded-xl border border-border bg-card p-4 shadow-lg"
        style={{
          top: rect ? Math.min(rect.bottom + 16, window.innerHeight - 180) : "50%",
          transform: rect ? `translate(-50%, 0)` : "translate(-50%, -50%)",
        }}
      >
        <h3 className="font-semibold text-foreground mb-1">{STEPS[step]?.title ?? ""}</h3>
        <p className="text-sm text-muted-foreground mb-4">{STEPS[step]?.description ?? ""}</p>
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={onSkip} className="text-muted-foreground">
            Skip
          </Button>
          {step < STEPS.length - 1 ? (
            <Button size="sm" onClick={onNext}>
              Next
            </Button>
          ) : (
            <Button size="sm" onClick={onSkip}>
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(content, document.body) : null;
}
